import { AuthBindings } from "@refinedev/core";
import axios from "axios";
import { UserRole } from "./models/role";

/**
 * Auth session contract:
 * - Access JWT (`TOKEN_KEY`) is opaque to the UI — never decode it for claims.
 * - UI identity comes from `USER_KEY`, populated by login / refresh / GET /auth/me bodies.
 * - Oversized JWTs (legacy fat account claims) are rejected by the edge before Nest;
 *   empty network failures with a stored token trigger session reset.
 */
export const TOKEN_KEY = "token";
export const REFRESH_TOKEN_KEY = "refreshToken";
export const TOKEN_EXPIRES_KEY = "tokenExpires";
export const USER_KEY = "user";
export const TENANT_SETTINGS_KEY = "tenantSettings";
export const ORDER_CREATE_STEPPED_KEY = "orderCreateSteppedMode";

/** Stashed admin session keys while impersonating a customer. */
export const IMPERSONATOR_TOKEN_KEY = "impersonatorToken";
export const IMPERSONATOR_REFRESH_TOKEN_KEY = "impersonatorRefreshToken";
export const IMPERSONATOR_TOKEN_EXPIRES_KEY = "impersonatorTokenExpires";
export const IMPERSONATOR_USER_KEY = "impersonatorUser";
export const IMPERSONATOR_TENANT_SETTINGS_KEY = "impersonatorTenantSettings";
export const IMPERSONATED_USER_LABEL_KEY = "impersonatedUserLabel";

const IMPERSONATOR_KEYS = [
  IMPERSONATOR_TOKEN_KEY,
  IMPERSONATOR_REFRESH_TOKEN_KEY,
  IMPERSONATOR_TOKEN_EXPIRES_KEY,
  IMPERSONATOR_USER_KEY,
  IMPERSONATOR_TENANT_SETTINGS_KEY,
  IMPERSONATED_USER_LABEL_KEY,
] as const;

export type SessionUser = {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: { id?: number | string; name?: string };
  account?: { id?: number; [key: string]: unknown };
  [key: string]: unknown;
};

export type ImpersonationLoginPayload = {
  token: string;
  refreshToken: string;
  tokenExpires: number;
  user: SessionUser;
  tenantSettings?: object | null;
};

/** Whether an admin session is currently stashed for impersonation exit. */
export const isImpersonating = (): boolean =>
  Boolean(localStorage.getItem(IMPERSONATOR_TOKEN_KEY));

/** Display label for the impersonated customer (banner). */
export const getImpersonatedUserLabel = (): string | null =>
  localStorage.getItem(IMPERSONATED_USER_LABEL_KEY);

/** Clear stashed impersonator keys without touching the active session. */
export const clearImpersonatorStash = (): void => {
  for (const key of IMPERSONATOR_KEYS) {
    localStorage.removeItem(key);
  }
};

/** Copy the current active session into impersonator stash keys. */
export const stashImpersonatorSession = (): void => {
  const token = localStorage.getItem(TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const tokenExpires = localStorage.getItem(TOKEN_EXPIRES_KEY);
  const user = localStorage.getItem(USER_KEY);
  const tenantSettings = localStorage.getItem(TENANT_SETTINGS_KEY);
  if (token) localStorage.setItem(IMPERSONATOR_TOKEN_KEY, token);
  if (refreshToken)
    localStorage.setItem(IMPERSONATOR_REFRESH_TOKEN_KEY, refreshToken);
  if (tokenExpires)
    localStorage.setItem(IMPERSONATOR_TOKEN_EXPIRES_KEY, tokenExpires);
  if (user) localStorage.setItem(IMPERSONATOR_USER_KEY, user);
  if (tenantSettings)
    localStorage.setItem(IMPERSONATOR_TENANT_SETTINGS_KEY, tenantSettings);
};

/** Apply a customer login payload as the active session. */
export const applyImpersonationSession = (
  payload: ImpersonationLoginPayload,
): void => {
  localStorage.setItem(TOKEN_KEY, payload.token);
  localStorage.setItem(REFRESH_TOKEN_KEY, payload.refreshToken);
  localStorage.setItem(TOKEN_EXPIRES_KEY, String(payload.tokenExpires));
  localStorage.setItem(USER_KEY, JSON.stringify(payload.user));
  if (payload.tenantSettings) {
    localStorage.setItem(
      TENANT_SETTINGS_KEY,
      JSON.stringify(payload.tenantSettings),
    );
  } else {
    localStorage.removeItem(TENANT_SETTINGS_KEY);
  }
  const label = [payload.user.firstName, payload.user.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  localStorage.setItem(
    IMPERSONATED_USER_LABEL_KEY,
    label || payload.user.email || String(payload.user.id),
  );
};

/**
 * Restore the stashed admin session as the active session and clear stash.
 * Returns false if no stash was present.
 */
export const restoreImpersonatorSession = (): boolean => {
  const token = localStorage.getItem(IMPERSONATOR_TOKEN_KEY);
  if (!token) {
    return false;
  }
  localStorage.setItem(TOKEN_KEY, token);
  const refreshToken = localStorage.getItem(IMPERSONATOR_REFRESH_TOKEN_KEY);
  const tokenExpires = localStorage.getItem(IMPERSONATOR_TOKEN_EXPIRES_KEY);
  const user = localStorage.getItem(IMPERSONATOR_USER_KEY);
  const tenantSettings = localStorage.getItem(IMPERSONATOR_TENANT_SETTINGS_KEY);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  if (tokenExpires) localStorage.setItem(TOKEN_EXPIRES_KEY, tokenExpires);
  if (user) localStorage.setItem(USER_KEY, user);
  if (tenantSettings) {
    localStorage.setItem(TENANT_SETTINGS_KEY, tenantSettings);
  } else {
    localStorage.removeItem(TENANT_SETTINGS_KEY);
  }
  clearImpersonatorStash();
  return true;
};

export interface ResetPasswordFormTypes {
  password: string;
  hash: string;
}

let isRefreshingToken = false;

const AuthUrls = [
  "/auth/refresh",
  "/auth/email/confirm",
  "/auth/account/register",
  "/auth/email/register",
  "/auth/forgot/password",
  "/auth/register/precheck",
  "/auth/email/login",
  // Covers /tenant-signup, /tenant-signup/activate and /tenant-signup/resend-activation
  // (isAuthUrl matches on substring).
  "/tenant-signup",
  "/users/accept-invitation",
];

/**
 * Machine-readable reasons a login can be refused. `authProvider.login` returns one of
 * these as `error.name` so the login page can translate it and decide whether to offer
 * a recovery link; `error.message` carries a plain-French fallback.
 */
export const LoginErrorCodes = [
  "tenantNotActivated",
  "trialExpired",
  "tenantSuspended",
  "userNotActivated",
  "accountNotActive",
  "invalidCredentials",
] as const;

export type LoginErrorCode = (typeof LoginErrorCodes)[number];

/** The subset of login codes the API can also raise on an ordinary authenticated call. */
export const TenantBlockCodes: string[] = [
  "tenantNotActivated",
  "trialExpired",
  "tenantSuspended",
];

/** Guards against a redirect storm when several requests fail at once. */
let tenantBlockRedirected = false;

/** Guards against repeated redirects when many requests fail with an empty edge response. */
let brokenAuthRecovered = false;

const FALLBACK_FR: Record<LoginErrorCode, string> = {
  tenantNotActivated: "Espace en attente d'activation",
  trialExpired: "Votre période d'essai est terminée",
  tenantSuspended: "Votre espace est suspendu",
  userNotActivated: "Compte en attente d'activation",
  accountNotActive: "Compte en attente d'activation",
  invalidCredentials: "Email ou mot de passe incorrect",
};

export function isAuthUrl(url: string): boolean {
  return AuthUrls.some(item => url.includes(item));
}

(function (XHR: any) {
  "use strict";

  var open = XHR.prototype.open;
  var send = XHR.prototype.send;

  XHR.prototype.open = function (method, url, async, user, pass) {
    this._url = url;
    open.call(this, method, url, async, user, pass);
  };

  XHR.prototype.send = function (data) {
    var self = this;
    var oldOnReadyStateChange;
    var url = this._url;
    const token = localStorage.getItem(TOKEN_KEY);
    if (
      token
      && url.includes(import.meta.env.VITE_API_URL)
      && !isAuthUrl(url)
    ) {
      this.setRequestHeader("authorization", "bearer " + token);
      //console.log("Setting token to", token);
    }

    function onReadyStateChange() {
      if (self.readyState == 4 /* complete */) {
        /* This is where you can put code that you want to execute post-complete*/
        /* URL is kept in this._url */
        if (
          isTokenExpired()
          && url.includes(import.meta.env.VITE_API_URL)
          && !isAuthUrl(url)
        ) {
          console.log("Token expired, we need to refresh it.", url);
          refreshToken();
        }
        // status 0 + stored token ≈ edge dropped the request (e.g. oversized JWT)
        if (
          self.status === 0
          && localStorage.getItem(TOKEN_KEY)
          && typeof url === "string"
          && url.includes(import.meta.env.VITE_API_URL)
          && !isAuthUrl(url)
        ) {
          recoverFromBrokenAuthSession();
        }
      }

      if (oldOnReadyStateChange) {
        oldOnReadyStateChange();
      }
    }

    /* Set xhr.noIntercept to true to disable the interceptor for a particular call */
    if (!this.noIntercept) {
      if (this.addEventListener) {
        this.addEventListener("readystatechange", onReadyStateChange, false);
      } else {
        oldOnReadyStateChange = this.onreadystatechange;
        this.onreadystatechange = onReadyStateChange;
      }
    }

    send.call(this, data);
  }
})(XMLHttpRequest);

export const axiosInstance = axios.create({ baseURL: import.meta.env.VITE_API_URL || "http://localhost:3000/api/v1" });

axiosInstance.interceptors.request.use(function (config) {
  console.log("Requesting", config.url);
  if (!config.url?.includes("/auth/refresh")) {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      //config.headers.Authorization = 'Bearer ' + token;
    }
  }
  return config;
}, function (error) {
  return Promise.reject(error);
});

const isTokenExpired = () => {
  const tokenExpires = localStorage.getItem(TOKEN_EXPIRES_KEY);
  if (tokenExpires) {
    //console.log("Token expires", tokenExpires, (new Date()).getTime());
    const expires = parseInt(tokenExpires);
    return expires < (new Date()).getTime();
  }
  return true;
}

export const refreshToken = async () => {

  if (isRefreshingToken) return;

  try {
    const token = localStorage.getItem(REFRESH_TOKEN_KEY);
    //console.log("Refresh Token", token);
    if (token) {
      isRefreshingToken = true;
      console.log("Trying to refresh token");
      const response = await axiosInstance.post("/auth/refresh", {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.setItem(TOKEN_KEY, response.data.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
      localStorage.setItem(TOKEN_EXPIRES_KEY, response.data.tokenExpires);
      localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
      // Store tenant settings if provided in refresh response
      if (response.data.tenantSettings) {
        localStorage.setItem(TENANT_SETTINGS_KEY, JSON.stringify(response.data.tenantSettings));
      }
      //console.log("Token refreshed", response.data.token);
      console.log("User", response.data.user);
    } else {
      resetSession();
      window.location.href = '/login';
    }
    isRefreshingToken = false;
  } catch (error) {
    console.error("Refresh token error", error);
    isRefreshingToken = false;
    resetSession();
    window.location.href = '/login';
  }
}


axiosInstance.interceptors.response.use(
  function (response) {
    // If the request succeeds, we don't have to do anything and just return the response
    return response;
  },
  async function (error) {
    if (isTokenExpired()) {
      //console.log("Token expired");
    }
    //console.log("Current token", localStorage.getItem(TOKEN_KEY));
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // We can check for a 401 status code and handle it
    if (error.response && error.response.status === 401
      && !error.config.url?.includes('/login')
      && !error.config.url?.includes('/refresh')) {
      // Clear the access token from local storage
      //refreshToken();
    }

    // The API answers 403 + { errors: { tenant } } when the caller's tenant is not
    // activated, has run out of trial, or is suspended. That can happen mid-session
    // because JWTs carry no tenant state, so drop the session and send the user to
    // /login where the matching explanation is rendered.
    const blocked = error.response?.status === 403
      && error.response?.data?.errors?.tenant;
    if (blocked
      && !tenantBlockRedirected
      && TenantBlockCodes.includes(blocked)) {
      tenantBlockRedirected = true;
      resetSession();
      window.location.href = `/login?blocked=${blocked}`;
    }

    // No HTTP response with a stored token ≈ oversized/invalid JWT dropped at the edge
    const requestUrl = String(error.config?.url ?? "");
    if (
      !error.response
      && localStorage.getItem(TOKEN_KEY)
      && !isAuthUrl(requestUrl)
      && !requestUrl.includes("/auth/me")
    ) {
      recoverFromBrokenAuthSession();
    }

    return Promise.reject(error);
  }
);

const resetSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRES_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TENANT_SETTINGS_KEY);
  clearImpersonatorStash();
};

const isCustomerRoleId = (roleId: unknown): boolean =>
  roleId === UserRole.user ||
  roleId === String(UserRole.user) ||
  Number(roleId) === UserRole.user;

const customerIdentityNeedsAccount = (user: SessionUser | null): boolean => {
  if (!user) {
    return false;
  }
  if (!isCustomerRoleId(user.role?.id)) {
    return false;
  }
  return user.account?.id == null;
};

/**
 * Load the current user from GET /auth/me and persist into USER_KEY.
 * Returns null when unauthenticated or the call fails.
 */
export const fetchCurrentUser = async (): Promise<SessionUser | null> => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return null;
  }
  try {
    const response = await axiosInstance.get("/auth/me", {
      headers: { Authorization: `Bearer ${token}` },
    });
    const user = response.data as SessionUser;
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    return user;
  } catch (error) {
    console.error("fetchCurrentUser error", error);
    return null;
  }
};

/**
 * When a customer session is missing account.id, refresh identity via /auth/me.
 */
export const ensureSessionUserAccount = async (
  user: SessionUser | null,
): Promise<SessionUser | null> => {
  if (!customerIdentityNeedsAccount(user)) {
    return user;
  }
  return (await fetchCurrentUser()) ?? user;
};

/** Clear session and send the user to login (fat JWT / empty edge response recovery). */
export const recoverFromBrokenAuthSession = (): void => {
  if (brokenAuthRecovered) {
    return;
  }
  brokenAuthRecovered = true;
  resetSession();
  if (!window.location.pathname.startsWith("/login")) {
    window.location.href = "/login";
  }
};

/**
 * Start impersonation: stash admin, apply customer session, then ensure
 * USER_KEY has account.id via /auth/me when the payload is incomplete.
 */
export const startImpersonationSession = async (
  payload: ImpersonationLoginPayload,
): Promise<void> => {
  stashImpersonatorSession();
  applyImpersonationSession(payload);
  await ensureSessionUserAccount(payload.user);
};

export const authProvider: AuthBindings = {

  login: async ({ username, email, password, remember }) => {
    if ((username || email) && password) {
      try {
        console.log('Trying to login', email, password);
        const response = await axiosInstance.post("/auth/email/login", {
          email,
          password,
          rememberMe: Boolean(remember),
        });
        console.log("Login response", response.data.tenantSettings);
        localStorage.setItem(TOKEN_KEY, response.data.token);
        localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refreshToken);
        localStorage.setItem(TOKEN_EXPIRES_KEY, response.data.tokenExpires);
        localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
        // Store tenant settings if provided in auth response
        if (response.data.tenantSettings) {
          localStorage.setItem(TENANT_SETTINGS_KEY, JSON.stringify(response.data.tenantSettings));
        }
        console.log("Tenant settings", response.data.tenantSettings);
        const user = response.data.user;
        console.log("User successfully logged with role", user.role);
        console.log("Response data", response.data);
        let redirecTo = "/";
        if (user?.role?.name === "User")
          redirecTo = "/account-home";
        if (user?.role?.name === "Admin" || user?.role?.name === "Superadmin")
          redirecTo = "/";
        if (user?.role?.name === "Tuner")
          redirecTo = "/orders";
        console.log("Redirecting to", redirecTo);
        window.location.href = redirecTo;
        return {
          success: false,
          //redirectTo: redirecTo,
        };

      } catch (error: any) {
        console.error("Login error", error.response?.data);
        const errors = error.response?.data?.errors ?? {};

        // error.name carries a stable machine code; the login page translates it and
        // decides whether to render a recovery link.
        let code: LoginErrorCode = "invalidCredentials";
        if (TenantBlockCodes.includes(errors.tenant)) {
          code = errors.tenant as LoginErrorCode;
        } else if (errors.user === "userNotActivated") {
          code = "userNotActivated";
        } else if (errors.account === "accountNotActive") {
          code = "accountNotActive";
        }

        return {
          success: false,
          error: { name: code, message: FALLBACK_FR[code] },
        };
      }
    }

    return {
      success: false,
      error: {
        name: "invalidCredentials",
        message: FALLBACK_FR.invalidCredentials,
      },
    };
  },

  logout: async () => {
    resetSession();
    return {
      success: true,
      redirectTo: "/login",
    };
  },

  forgotPassword: async ({ email }) => {
    try {
      await axiosInstance.post("/auth/forgot/password", { email });
      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error) {
      return {
        success: false,
        error: {
          name: "Erreur d'authentification",
          message: "Invalid email address",
        },
      };
    }
  },

  check: async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      return {
        authenticated: true,
      };
    }

    return {
      authenticated: false,
      redirectTo: "/login",
    };
  },

  getPermissions: async () => {
    const user = localStorage.getItem(USER_KEY);
    if (user) {
      try {
        const parsed = JSON.parse(user || "{}");
        console.log("User data", parsed);
        console.log("getPermissions - User role", parsed.role?.id);
        return String(parsed.role?.id) || '';
      } catch (error) {
        console.error("Error parsing user data", error);
      }
    }
    return '';
  },

  getIdentity: async () => {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw) as SessionUser;
      return await ensureSessionUserAccount(parsed);
    } catch (error) {
      console.error("Error parsing user data", error);
      return null;
    }
  },

  updatePassword: async ({ password, hash }) => {
    console.log("Update password", password, hash);
    if (!password || !hash) {
      return {
        success: false,
        error: {
          name: "Erreur d'authentification",
          message: "Invalid password or hash",
        },
      };
    }

    try {
      await axiosInstance.post("/auth/reset/password", { password, hash });
      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error) {
      return {
        success: false,
        error: {
          name: "Erreur d'authentification",
          message: "Invalid password or hash",
        },
      };
    }
  },

  onError: async (error) => {
    console.error("Authentication error", error);
    if (error.response?.status === 401) {
      refreshToken();
    }
    return { error };
  },
};
